




































































































































































































































































/* eslint-disable no-underscore-dangle */
import { defineComponent, computed, onMounted, watch, ref } from '@vue/composition-api';
import filesize from 'filesize';
import { Location } from 'vue-router';

import { prompt } from '@/components/Prompt';
import store from '@/store';
import type { Task } from '@/types';
import { useClient } from '@/use';
import { taskIsFinished } from '@/utils';

import { completedAnalysesOpen, tasks, ongoingTasks } from '../state';

export default defineComponent({
  name: 'CompletedAnalysesMenu',
  setup() {
    const selectedTasks = ref<Task[]>([]);
    const fetchingTasks = ref(false);
    const loading = computed(() => ongoingTasks.value.length > 0 || fetchingTasks.value);
    const latestAddedTaskId = computed(() => store.state.application.latestAddedTaskId);
    const search = ref<string | null>(null);
    const taskFetchTimeout = ref<number | null>(null);

    // Conditionally add motion header
    const isSupervisor = computed(() => store.getters.isSupervisor);
    const headers = computed(() => {
      const val = [
        { text: 'Video filename', value: 'video_filename' },
        { text: 'Status', value: 'status' },
        { text: 'Size', value: 'size', sortable: false },
        { text: 'Motion', value: 'motion', sortable: false },
        { text: 'Created', value: 'requested_at', sort: (a: string, b: string) => Date.parse(b) - Date.parse(a) },
        { text: 'Results', value: 'results', sortable: false },
      ];

      // If not allowed, remove motion column
      if (!isSupervisor.value) {
        val.splice(val.findIndex((v) => v.value === 'motion'), 1);
      }

      return val;
    });

    async function fetchTasks() {
      // Only one instance of this function should run at a time
      if (taskFetchTimeout.value !== null) {
        return;
      }

      const retrieve = async () => {
        fetchingTasks.value = true;
        const userTasks = await useClient().fetchUserTasks();
        fetchingTasks.value = false;

        tasks.value = userTasks;
      };

      // Initially retrieve tasks
      await retrieve();

      // Check tasks routinely until they are all completed
      while (ongoingTasks.value.length) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => {
          // Set taskFetchTimeout to establish mutex
          taskFetchTimeout.value = window.setTimeout(r, 5000);
        });

        // Set timeout to null to release mutex
        taskFetchTimeout.value = null;

        // Retrieve tasks again
        // eslint-disable-next-line no-await-in-loop
        await retrieve();
      }

      // No more ongoing tasks, ensure this is null
      taskFetchTimeout.value = null;
    }

    function closeDialog() {
      completedAnalysesOpen.value = false;
    }

    // If a task is added, re-fetch tasks
    watch(latestAddedTaskId, () => {
      fetchTasks();
    });

    // Reset search when dialog opened
    watch(completedAnalysesOpen, (val) => {
      if (val) {
        search.value = null;
      }
    });

    function displayProgress(item: Task) {
      if (taskIsFinished(item)) {
        return '';
      }

      return `${item.status.progress}%`;
    }

    function displaySize(item: Task, zip = false) {
      const { result } = item.status;
      if (!result) {
        return 'N/A';
      }

      const size = zip ? result.zip_size : result.size;
      if (Number.isNaN(Number(size))) {
        return 'N/A';
      }

      return filesize(size);
    }

    const datasetActionsDisabled = (item: Task) => item.status.result === undefined;
    function taskDownloadLink(item: Task, zip = false): string | null {
      if (item.status.result === undefined) {
        return null;
      }

      const filename = zip ? item.status.result.zip_filename : item.status.result.filename;
      return useClient().getDownloadLink(filename);
    }

    function openDatasetLocation(task: Task): Location | undefined {
      const datasetId = task.status.result?.dataset_id;
      if (datasetId === undefined) {
        return undefined;
      }

      return {
        name: 'Home',
        query: {
          dataset: datasetId,

          // Always true, since task results are per user
          user: 'true',
        },
      };
    }

    async function deleteTask(task: Task, skipDialog = false) {
      const choice = skipDialog || await prompt.open({
        title: 'Delete this Task?',
        message: 'The task will be deleted from this list, '
        + 'but the associated dataset can still be accessed '
        + 'through the dataset browser, or with its URL.',
        positiveText: 'Yes',
        negativeText: 'No',
      });

      if (choice) {
        await useClient().deleteTask(task);
        fetchTasks();
      }
    }

    function formatDateTime(dateString: string): string {
      const timestamp = Date.parse(dateString);
      if (Number.isNaN(timestamp)) {
        return '';
      }
      const date = new Date(dateString);
      return date.toLocaleString();
    }

    // Run anything necessary on component mount
    onMounted(() => {
      fetchTasks();
    });

    function downloadSelectedTasks(zip = false) {
      selectedTasks.value.forEach((task) => {
        const link = taskDownloadLink(task, zip);
        if (link === null) {
          return;
        }

        window.open(link, '_blank');
      });
    }

    async function deleteSelectedTasks() {
      const proceed = await prompt.open({
        title: 'Delete all selected tasks?',
        message: 'The tasks will be deleted from this list, '
        + 'but the associated dataset can still be accessed '
        + 'through the dataset browser, or with its URL.',
        positiveText: 'Yes',
        negativeText: 'No',
      });
      if (!proceed) {
        return;
      }

      fetchingTasks.value = true;

      await Promise.all(selectedTasks.value.map((task) => deleteTask(task, true)));
      selectedTasks.value = [];
      await fetchTasks();

      fetchingTasks.value = false;
    }

    return {
      headers,
      isSupervisor,
      completedAnalysesOpen,
      tasks,
      selectedTasks,
      downloadSelectedTasks,
      deleteSelectedTasks,
      ongoingTasks,
      search,
      loading,
      closeDialog,
      taskDownloadLink,
      datasetActionsDisabled,
      openDatasetLocation,
      deleteTask,
      displayProgress,
      displaySize,
      formatDateTime,
    };
  },
});
